import React from 'react';
import { formatFileSize } from '../data/documents';
import '../styles/DocumentCard.css';

type DocTapeColor = 'sage' | 'terracotta' | 'ochre';

/**
 * Minimal shape the card needs to render. `InstitutionalDocument` (transparency
 * archive) and `VolunteerDocument` (volunteer docs on /ajudar) both satisfy it.
 */
export interface CardDocument {
  id: string;
  title: string;
  /** Optional issue year — omitted for documents that aren't dated. */
  year?: number | null;
  url: string;
  sizeBytes: number;
}

interface DocumentCardProps {
  doc: CardDocument;
  tapeColor?: DocTapeColor;
  /** Visual rotation in degrees. Default is a small deterministic angle. */
  rotation?: number;
}

const TAPE_VALUES: Record<DocTapeColor, string> = {
  sage: 'rgba(122,139,95,.55)',
  terracotta: 'rgba(194,90,58,.55)',
  ochre: 'rgba(198,140,59,.55)',
};

interface CardStyle extends React.CSSProperties {
  '--rot'?: string;
  '--tape-bg'?: string;
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  doc,
  tapeColor = 'sage',
  rotation,
}) => {
  const r = rotation ?? rotationForId(doc.id);
  const style: CardStyle = {
    '--rot': `${r}deg`,
    '--tape-bg': TAPE_VALUES[tapeColor],
  };

  return (
    <a
      className="doc-card"
      style={style}
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Abrir ${doc.title}${doc.year ? ` (${doc.year})` : ''} — PDF, ${formatFileSize(doc.sizeBytes)}`}
    >
      <div className="doc-card__tape" aria-hidden="true" />
      <div className="doc-card__kind">— PDF</div>
      <h3 className="doc-card__title">{doc.title}</h3>
      {doc.year != null && <div className="doc-card__year">{doc.year}</div>}
      <div className="doc-card__meta">
        <span className="doc-card__size">{formatFileSize(doc.sizeBytes)}</span>
        <span className="doc-card__arrow" aria-hidden="true">↗</span>
      </div>
    </a>
  );
};

function rotationForId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const r = ((h % 300) - 150) / 100; // -1.50 .. +1.50
  return Number(r.toFixed(2));
}

export default DocumentCard;
